import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface PageErrorProps {
  error: Error | string | null | undefined;
  retry?: () => void;
  className?: string;
}

/**
 * PR-N8 OVERNIGHT MAY 11 — standardized friendly error block.
 *
 * Replaces the previous raw red Prisma-error banners. The technical detail
 * is collapsed inside <details> so support can still see it but the user
 * isn't blocked by SQL gibberish.
 */
export function PageError({ error, retry, className }: PageErrorProps) {
  const msg = error
    ? (typeof error === 'string' ? error : (error.message ?? 'Unknown error'))
    : null;
  return (
    <div className={'rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-2 ' + (className ?? '')}>
      <div className="inline-flex items-center gap-2 text-amber-700 dark:text-amber-300">
        <AlertCircle className="h-4 w-4" />
        <strong>Something went wrong</strong>
      </div>
      <p className="text-sm text-foreground">
        We couldn&apos;t load this page right now. Try again in a moment, or contact support if it persists.
      </p>
      {retry && (
        <div>
          <Button size="sm" variant="outline" onClick={retry}>Try again</Button>
        </div>
      )}
      {msg && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer select-none">Technical details</summary>
          <pre className="mt-1 whitespace-pre-wrap break-all">{msg}</pre>
        </details>
      )}
    </div>
  );
}
